import { KeyboardLayouts } from '../../../SimpleKeyboard/Layout';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { ChangeEvent, useRef, FocusEvent, useEffect, memo } from 'react';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { useField, useFormikContext } from 'formik';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { PricePatternMask } from '../PricePatternMask/PricePatternMask';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { KioskFormPriceFieldProps } from '../FormPriceField.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormPriceField = memo(({
    name,
    id,
    title,
    label,
    ariaLabel,
    disabled,
    required,
    autoFocus,
    keyboardLayout = KeyboardLayouts.Price,
    shiftAfterSpace,
    shiftAfterPeriod,
    shiftAfterAmpersand,
    hideKeyboardOnClickOut,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
    tabFocusRefContext,
    externalRef,
    onChangeCallback,
    onBlurCallback,
    onFocusCallback,
}: KioskFormPriceFieldProps) => {
    /**
     * Not all formik validation accounts for TextFields that are null.
     * So even though we pass an undefined validation type to the function,
     * it is still required in order to have null required fields validate.
     */
    const [field, meta] = useField<string>({
        name,
        validate: TextFieldValidation({
            required: required ?? false,
            label,
            name,
            validationType: undefined,
        }),
    });
    const { submitCount, setFieldValue } = useFormikContext();

    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    /**
     * The input value ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal value state.
     */
    const valueRef = useRef<string>(field.value ?? '');
    /**
     * The caret position ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal caret position state.
     */
    const caretPositionRef = useRef<number | null>(null);
    /**
     * The focus ref to prevent a stale state when the on-screen keyboard
     * calls any functions that need to know if the field is focused or not.
     */
    const isFocused = useRef<boolean>(false);
    /**
     * The on-screen keyboard input ref is used to prevent a stale state when we call to update it
     * from keyboard context functions. It is used to prevent the onChange function that recieves a
     * callback from the pattern mask from resetting the caret position incorrectly after we set it
     * using the keyboard context setValue function.
     */
    const wasOSKInput = useRef<boolean>(false);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const locale = useLocale();
    const {
        dealer: { primaryLanguage },
    } = useKioskState();

    useEffect(() => {
        /**
         * Since the price field has external uses that use a normal ref,
         * we need to ensure that the external ref stays in sync with the
         * internal ref we use to manage the controls state.
         */
        if (externalRef && inputRef.current) {
            externalRef.current = inputRef.current;
        }
    }, [externalRef]);

    useEffect(() => {
        if (tabFocusRefContext) {
            tabFocusRefContext.current = inputRef.current;
        }
    }, [tabFocusRefContext]);

    useEffect(() => {
        /**
         * Updates the internal caret position when the internal MUI value finishes changing from the
         * pattern mask.
         */
        if (inputRef.current) {
            inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);

            /**
             * We don't want the pattern mask to incorrectly update the caret position,
             * so if the on-screen keyboard was used, this will not trigger.
             */
            if (wasOSKInput.current) {
                wasOSKInput.current = false;
            }
        }
    }, [inputRef.current?.value]);

    useEffect(() => {
        if (field.value !== valueRef.current) {
            valueRef.current = field.value;

            onChangeCallback && onChangeCallback(field.value);
        }
    }, [field.value, onChangeCallback]);


    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const getValue = (): GetValueReturn => {
        /**
         * We initialize the wasOSKInput ref here to be true so that onChange doesn't
         * set a new caret position when it runs.
         */
        wasOSKInput.current = true;

        if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
            const selectionStart: number = inputRef.current.selectionStart;
            const selectionEnd: number = inputRef.current.selectionEnd;

            return {
                inputValue: valueRef.current,
                caretPositionStart: selectionStart,
                caretPositionEnd: selectionEnd,
            };
        }
        else {
            return {
                inputValue: valueRef.current,
                caretPositionStart: valueRef.current.length,
                caretPositionEnd: valueRef.current.length,
            };
        }
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const setValue = ({ value, caretPosition }: SetValueProps): void => {
        caretPositionRef.current = caretPosition;
        setFieldValue(name, value);
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
        /**
         * Removes the decimal of a backspace would cause it to be the only value remaining
         */
        if (wasBackspace && value.length === caretPosition && value[value.length - 1] === '.') {
            value = value.slice(0, value.length - 1);
            caretPosition = caretPosition - 1;
        }
        /**
         * Removes additional decimals
         */
        if (!wasBackspace && value.split('.').length - 1 > 1) {
            value = value.slice(0, caretPosition - 1) + value.slice(caretPosition);
            caretPosition = caretPosition - 1;
        }
        /**
         * Prevents typing beyond the 2 numbers past the decimal
         */
        if (!wasBackspace && (/^\d*\.{1}\d{3}$/).test(value)) {
            value = value.slice(0, caretPosition - 1) + value.slice(caretPosition);
            caretPosition = caretPosition - 1;
        }

        return {
            value: value,
            caretPosition: caretPosition,
        };
    };

    /**
     * Used by the keyboard context and is described on the declaration there.
     */
    const onKeyboardInput = (): void => {
        if (inputRef.current && !isFocused.current) {
            inputRef.current.focus();
        }
    };

    const { onFocus, onBlur } = useKeyboardIntegration({
        fieldValue: field.value,
        getValue: getValue,
        setValue: setValue,
        onInputChange: onInputChange,
        onKeyboardInput: onKeyboardInput,
        keyboardLayout: keyboardLayout,
        shiftAfterPeriod: shiftAfterPeriod,
        shiftAfterSpace: shiftAfterSpace,
        shiftAfterAmpersand: shiftAfterAmpersand,
        hideKeyboardOnClickOut: hideKeyboardOnClickOut,
        capsLock: capsLock,
    });

    const handleOnBlur = () => {
        onBlur();
        onBlurCallback && onBlurCallback();
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        /**
         * We don't want the onAccept from the mask to update anything when
         * the on-screen keyboard is used, so if we use the on-screen keyboard
         * we skip this section of code.
         */
        if (!wasOSKInput.current) {
            caretPositionRef.current = event.target.selectionEnd;
        }
        setFieldValue(name, event.target.value);
    };

    const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onFocus(event);
        onFocusCallback && onFocusCallback();
    };

    return (
        <BaseTextField
            name={name}
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            inputMode={'text'}
            type={'text'}
            inputRef={inputRef}
            value={field.value ?? ''}
            error={meta.error}
            touched={meta.touched || submitCount > 0}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            capsLock={capsLock}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            InputProps={{
                inputComponent: PricePatternMask as any,
            }}
        />
    );
});
